import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { setUserObject } from '@grandvisionhq/analytics';
import { Actions, Selectors, State, useMyAccountMenuItems, MyAccountMenuPosition, MyAccountMenu } from '@grandvisionhq/auth';
import { useLocation } from '@grandvisionhq/frk-routing';
import { getLocaleSwitcherMenuLabel, getLocaleSwitcherData } from '@grandvisionhq/layout';
import { useIntl, useFeatureToggles, useDispatch, useSelector } from '@grandvisionhq/state';
import { CmsHeader as CmsHeader$1 } from '../../components/Header/Header.js';
import { useBrPageContext, useBrComponentContext } from '../../context/context.js';
import '@bloomreach/react-sdk';
import '../../context/configuration.js';
import { useMeta } from '../../context/MetaContext.js';
import '@bloomreach/spa-sdk';
import { getUrl } from '../../utils/get-url.js';
import '@grandvisionhq/search';
import 'jsonpointer';
import '@grandvisionhq/utils';
import '@grandvisionhq/common';
import '../../utils/http-client.js';
import { useBloomreachAppointmentURLsConfiguration } from '../../hooks/use-bloomreach-appointment-urls-configuration.js';
import { useBloomreachPageModels } from '../../hooks/use-bloomreach-page-models.js';
import '@grandvisionhq/lib-utils-html';
import '@grandvisionhq/content';
import '@grandvisionhq/graphql';
import '../../operations/mutations/signup-promotion.js';
import '../../operations/mutations/subscribe-newsletter.js';
import '../../operations/mutations/subscribe-newsletter-opt-in.js';
import '@grandvisionhq/svc-tealium/events';
import { useBloomreachPageRoutes } from '../../hooks/use-bloomreach-page-routes.js';
import { useBloomreachSiteLogos } from '../../hooks/use-bloomreach-site-logos.js';
import '../../hooks/use-breakpoint-visibility.js';
import { getMenuItems } from '../../utils/get-menu-items.js';

const CmsHeader = (props)=>{
    const intl = useIntl();
    const page = useBrPageContext();
    const component = useBrComponentContext();
    const { isMultilingualEnabled , isMyAccountEnabled , isAABEnabled , isWishlistEnabled  } = useFeatureToggles();
    const { site , bloomreach , algolia  } = useMeta();
    const siteLogos = useBloomreachSiteLogos();
    const isPreview = Boolean(page === null || page === void 0 ? void 0 : page.isPreview());
    const { routes: pageRoutes  } = useBloomreachPageRoutes();
    const { pathname  } = useLocation();
    var ref2;
    const siteName = (ref2 = site === null || site === void 0 ? void 0 : site.name) !== null && ref2 !== void 0 ? ref2 : 'Unknown';
    var ref1;
    const logoLink = (ref1 = getUrl(pageRoutes.root, page)) !== null && ref1 !== void 0 ? ref1 : '/';
    /* My account actions */ const dispatch = useDispatch();
    const logout = React.useCallback(()=>dispatch(Actions.LogoutActions.logOut(pathname))
    , [
        dispatch,
        pathname
    ]);
    const recoverSession = React.useCallback(()=>dispatch(Actions.LoginActions.recoverSession())
    , [
        dispatch
    ]);
    /* My account state */ const auth = useSelector(Selectors.getAuth);
    const formattedRoutes = useSelector(Selectors.getFormattedRoutes);
    /* State */ const [mounted, setMounted] = React.useState(false);
    /* Create all menu data with PageModel */ const pageModels = useBloomreachPageModels();
    const { routes ={} , localeOptions , countrySwitcher  } = pageModels !== null && pageModels !== void 0 ? pageModels : {};
    const { menuItems: topMenuItems , menu: topMenu  } = getMenuItems('top-menu', component, page);
    const { menu: appointmentMenu , menuItems: appointmentMenuItems  } = getMenuItems('appointment-menu', component, page);
    const { menu: mainMenu , menuItems: mainMenuItems  } = getMenuItems('main-menu', component, page);
    const appointmentMenuItemsWithAABconfig = useBloomreachAppointmentURLsConfiguration(appointmentMenuItems);
    /* Country switcher - this is not the language/locale switcher */ const countrySwitcherContent = countrySwitcher && (page === null || page === void 0 ? void 0 : page.getContent(countrySwitcher));
    const countrySwitcherOptions = getCountrySwitcherUrls(countrySwitcherContent === null || countrySwitcherContent === void 0 ? void 0 : countrySwitcherContent.getData());
    const headerLogo = React.useMemo(()=>siteLogos.main ? {
            url: siteLogos.main,
            altText: siteName
        } : undefined
    , [
        siteLogos.main,
        siteName
    ]);
    const headerLogoMobile = React.useMemo(()=>siteLogos.mobile ? {
            url: siteLogos.mobile,
            altText: siteName
        } : undefined
    , [
        siteLogos.mobile,
        siteName
    ]);
    /* My account menu setup */ const authRef = React.useRef(auth);
    const isUserLoggedIn = State.getLoggedInState(auth) === 'logged-in';
    const myAccountMenuItems = useMyAccountMenuItems({
        logout,
        position: MyAccountMenuPosition.Header,
        routes: formattedRoutes,
        isUserLoggedIn,
        isAabEnabled: isAABEnabled,
        isWishlistEnabled
    })/**
     * Providing an `onClick` here would mean that we support
     * client-side routing, which we don't (yet). Once the framework
     * routing is in place for MyAccount, this mapping can be removed.
     */ .map((item)=>swcHelpers.objectSpread({}, item, {
            link: item.link ? swcHelpers.objectSpread({}, item.link, {
                onClick: undefined
            }) : undefined
        })
    );
    const myAccountButton = React.useMemo(()=>{
        if (!isMyAccountEnabled || !formattedRoutes) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(MyAccountMenu, {
            menuItems: myAccountMenuItems
        });
    }, [
        myAccountMenuItems,
        isMyAccountEnabled,
        formattedRoutes
    ]);
    const ordersUrl = React.useMemo(// ordersUrl should be defined when my-account is enabled and the orders route exists
    ()=>{
        return isMyAccountEnabled ? formattedRoutes === null || formattedRoutes === void 0 ? void 0 : formattedRoutes.orders : undefined;
    }, [
        formattedRoutes === null || formattedRoutes === void 0 ? void 0 : formattedRoutes.orders,
        isMyAccountEnabled
    ]);
    const withMyAccountmenuItems = useWithMyAccountMenuItems(mainMenuItems, intl, formattedRoutes, logout, isUserLoggedIn, isAABEnabled, isWishlistEnabled);
    const navMenuItems = React.useMemo(()=>{
        const menuItems = isMyAccountEnabled ? withMyAccountmenuItems : mainMenuItems;
        const result = isMultilingualEnabled ? [
            ...menuItems,
            createLocaleSwitcherMenuStructure(localeOptions || [], intl)
        ] : menuItems;
        // TODO: this should be changed when the upgrade to BR 14.5 is done
        const previewLinkPrefix = `/site/_cmsinternal/${bloomreach.channel}`;
        // Check if a sitenav item's url corresponds with the current url path
        const currentPath = pathname === null || pathname === void 0 ? void 0 : pathname.split('?')[0];
        const activeItems = result.filter((menuItem)=>{
            var ref;
            return checkIfActive(currentPath, (((ref = menuItem.link) === null || ref === void 0 ? void 0 : ref.href) || '').replace(previewLinkPrefix, ''));
        });
        const active = activeItems.sort((a, b)=>{
            var ref, ref3;
            return isMoreSpecific((ref = a.link) === null || ref === void 0 ? void 0 : ref.href, (ref3 = b.link) === null || ref3 === void 0 ? void 0 : ref3.href) ? -1 : 1;
        }).shift();
        if (active === null || active === void 0 ? void 0 : active.link) {
            active.link = swcHelpers.objectSpread({}, active.link, {
                active: true
            });
        }
        return result;
    }, [
        isMyAccountEnabled,
        myAccountMenuItems,
        mainMenu,
        page,
        isMultilingualEnabled,
        localeOptions,
        intl,
        pathname,
        bloomreach.channel, 
    ]);
    /**
   * Search callback
   */ const handleSearch = React.useCallback((query, type)=>{
        if (!algolia) {
            return;
        }
        const { indexes , productIndex  } = algolia;
        const storeIndex = indexes === null || indexes === void 0 ? void 0 : indexes.find((index)=>index.type === 'stores'
        );
        const contentIndex = indexes === null || indexes === void 0 ? void 0 : indexes.find((index)=>index.type === 'content'
        );
        switch(type){
            case 'products':
                window.location.assign(`${productIndex.targetPath}${encodeURIComponent(query)}`);
                break;
            case 'stores':
                storeIndex && window.location.assign(`${storeIndex.targetPath}${encodeURIComponent(query)}`);
                break;
            case 'content':
                contentIndex && window.location.assign(`${contentIndex.targetPath}${encodeURIComponent(query)}#pages`);
                break;
            default:
                console.warn(`Trying to perform search action for unknown Algolia index type: ${type}`);
        }
    }, [
        algolia
    ]);
    React.useEffect(()=>{
        // Runs on mount and updates
        if ((!mounted || State.isUninitialized(authRef.current)) && State.isInitialized(auth)) {
            recoverSession();
        }
        setMounted(true);
        authRef.current = auth;
        // Let analytics know if user is logged in
        setUserObject(isUserLoggedIn);
    }, [
        mounted,
        auth,
        recoverSession,
        isUserLoggedIn
    ]);
    if ((!topMenu || !topMenuItems) && (!mainMenu || !mainMenuItems)) {
        console.error('No configuration or components found for Header in CMS model!');
        return null;
    }
    if (!headerLogo || !headerLogoMobile) return null;
    return /*#__PURE__*/ React.createElement(CmsHeader$1, swcHelpers.extends({}, props, {
        headerLogoLink: logoLink,
        headerMenuModel: topMenu,
        headerMenuItems: topMenuItems,
        navMenuModel: mainMenu,
        navMenuItems: navMenuItems,
        appointmentMenuModel: appointmentMenu,
        appointmentMenuItems: appointmentMenuItemsWithAABconfig,
        myAccountButton: myAccountButton,
        onSubmitSearch: handleSearch,
        preview: isPreview,
        availableLocales: localeOptions,
        countrySwitcherOptions: countrySwitcherOptions,
        headerLogo: headerLogo,
        headerLogoMobile: headerLogoMobile,
        pageRoutes: routes,
        algoliaHeaderConfig: algolia,
        ordersUrl: ordersUrl
    }));
};
function isMoreSpecific(nextLink = '', prevLink = '') {
    const isEqual = (nextLink.match(/\//g) || []).length === (prevLink.match(/\//g) || []).length;
    if (isEqual) {
        return nextLink.length > prevLink.length;
    } else {
        return (nextLink.match(/\//g) || []).length > (prevLink.match(/\//g) || []).length;
    }
}
function checkIfActive(currentPath = '', itemPath = '') {
    // root c.q. home
    if (currentPath === '/' && itemPath === '/') {
        return true;
    } else {
        return currentPath.startsWith(itemPath);
    }
}
const createLocaleSwitcherMenuStructure = (availableLocales, intl)=>{
    const localeSwitcherMenuLabel = getLocaleSwitcherMenuLabel(intl.locale);
    const localeMenuItems = getLocaleSwitcherData(availableLocales, false);
    const activeLocale = availableLocales.find((item)=>item.locale.replace('_', '-') === intl.locale
    );
    return {
        label: localeSwitcherMenuLabel,
        parameters: {
            menuKey: 'locale-switcher'
        },
        childMenuItems: [
            {
                label: activeLocale ? activeLocale.chooseLanguage : localeSwitcherMenuLabel,
                childMenuItems: localeMenuItems
            }, 
        ]
    };
};
const getCountrySwitcherUrls = (content)=>{
    if (!(content === null || content === void 0 ? void 0 : content.imageLinkList)) {
        return undefined;
    }
    var ref, ref4, ref5;
    return content.imageLinkList.map((item)=>{
        var ref9, ref6, ref7, ref8;
        return {
            image: (ref = (ref9 = item.image) === null || ref9 === void 0 ? void 0 : ref9.url) !== null && ref !== void 0 ? ref : '',
            label: (ref4 = (ref6 = item.link) === null || ref6 === void 0 ? void 0 : ref6.label) !== null && ref4 !== void 0 ? ref4 : '',
            url: (ref5 = (ref7 = item.link) === null || ref7 === void 0 ? void 0 : ref7.url) !== null && ref5 !== void 0 ? ref5 : '',
            openInNewWindow: ((ref8 = item.link) === null || ref8 === void 0 ? void 0 : ref8.openInNewWindow) || false
        };
    });
};
const useCreateMyAccountMobileMenuStructure = (intl, formattedRoutes, logout, isLoggedIn, isAABEnabled, isWishlistEnabled)=>{
    const myAccountMenuItems = useMyAccountMenuItems({
        logout,
        routes: formattedRoutes,
        isUserLoggedIn: isLoggedIn,
        isAabEnabled: isAABEnabled,
        isWishlistEnabled
    });
    const submenu = [
        {
            label: '',
            childMenuItems: formattedRoutes ? myAccountMenuItems : []
        }, 
    ];
    return {
        label: intl.getLabel('account.menu.myAccount'),
        link: {
            href: '/',
            type: 'external'
        },
        parameters: {
            menuKey: 'my-account'
        },
        childMenuItems: submenu
    };
};
const useWithMyAccountMenuItems = (navMenuItems, intl, formattedRoutes, logout, isLoggedIn, isAABEnabled, isWishlistEnabled)=>{
    const myAccountMenuItems = useCreateMyAccountMobileMenuStructure(intl, formattedRoutes, logout, isLoggedIn, isAABEnabled, isWishlistEnabled);
    return [
        ...navMenuItems || [],
        myAccountMenuItems
    ];
};

export { CmsHeader as default };
//# sourceMappingURL=Header.js.map
